import React, { useState, useEffect } from 'react';
import { Modal, Slider, Select, InputNumber } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';
import { CancelButton, CreateButton } from '../../buttons';

// Types
export interface AnswerSheetFormData {
  name: string;
  paperSize: 'Letter' | 'A4' | 'Legal';
  numberOfQuestions: number;
  numberOfAnswers: 3 | 4 | 5;
  answerKeys: 'A-E' | 'a-e' | '1-5';
  bubbleColor: 'Blue' | 'Black' | 'Red' | 'Green';
}

// Validation: Get max questions based on paper size and number of bubbles
const getMaxQuestions = (paperSize: string, numberOfBubbles: number): number => {
  if (paperSize === 'Letter') {
    return numberOfBubbles === 5 ? 188 : 190;
  } else if (paperSize === 'A4') {
    return numberOfBubbles === 5 ? 160 : 200;
  } else if (paperSize === 'Legal') {
    return numberOfBubbles === 5 ? 190 : 200;
  }
  return 200;
};

interface CreateAnswerSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AnswerSheetFormData) => void;
  isLoading?: boolean;
}

// Animations
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components (matching CreateClassModal style)
const ModalContent = styled.div`
  padding: 20px 20px 16px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const IconContainer = styled.div`
  margin-bottom: 12px;
`;

const CreateIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto;
  background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  box-shadow: 0 6px 16px rgba(31, 162, 255, 0.25);
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: right;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font-size: 14px;
  color: #1a1a1a;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: #1677ff;
  }
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }
`;

const SegmentGroup = styled.div`
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  width: 100%;
  height: 40px;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 100%;
  padding: 0 12px;
  border: none;
  background: ${props => props.$active ? 'linear-gradient(135deg, rgba(31, 162, 255, 0.15) 0%, rgba(18, 216, 250, 0.15) 100%)' : 'transparent'};
  color: ${props => props.$active ? '#1FA2FF' : '#666'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: 1px solid rgba(0, 0, 0, 0.15);
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, rgba(31, 162, 255, 0.15) 0%, rgba(18, 216, 250, 0.15) 100%)' 
      : 'rgba(0, 0, 0, 0.04)'};
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 40px;
  
  .ant-slider {
    flex: 1;
    margin: 0;
    
    .ant-slider-track {
      background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
    }
    
    .ant-slider-handle {
      border-color: #1FA2FF;
      
      &:hover, &:focus {
        border-color: #12D8FA;
        box-shadow: 0 0 0 5px rgba(31, 162, 255, 0.12);
      }
    }
  }
`;

const NumberInputStyled = styled(InputNumber<number>)`
  width: 70px;
  height: 40px;
  
  .ant-input-number-input-wrap {
    height: 100%;
  }
  
  .ant-input-number-input {
    height: 38px;
    text-align: center;
    font-weight: 500;
  }
  
  &.ant-input-number {
    border-radius: 8px;
    border-color: rgba(0, 0, 0, 0.15);
    
    &:hover {
      border-color: #1677ff;
    }
    
    &:focus, &.ant-input-number-focused {
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 40px;
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    transition: all 0.2s ease;
    
    .ant-select-selection-item {
      line-height: 38px !important;
    }
  }
  
  &:hover .ant-select-selector {
    border-color: #1677ff !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #1677ff !important;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1) !important;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

// Constants
const PAPER_SIZES = ['Letter', 'A4', 'Legal'] as const;
const NUMBER_OF_ANSWERS = [3, 4, 5] as const;
const ANSWER_KEYS = ['A-E', 'a-e', '1-5'] as const;
const BUBBLE_COLORS = [
  { value: 'Blue', label: 'Blue' },
  { value: 'Black', label: 'Black' },
  { value: 'Red', label: 'Red' },
  { value: 'Green', label: 'Green' },
];

export const CreateAnswerSheetModal: React.FC<CreateAnswerSheetModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<AnswerSheetFormData>({
    name: '',
    paperSize: 'A4',
    numberOfQuestions: 100,
    numberOfAnswers: 4,
    answerKeys: 'a-e',
    bubbleColor: 'Black'
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        paperSize: 'A4',
        numberOfQuestions: 100,
        numberOfAnswers: 4,
        answerKeys: 'a-e',
        bubbleColor: 'Black'
      });
    }
  }, [isOpen]);

  // Auto-adjust questions when paper size or number of bubbles changes
  const maxQuestions = getMaxQuestions(formData.paperSize, formData.numberOfAnswers);
  
  useEffect(() => {
    if (formData.numberOfQuestions > maxQuestions) {
      setFormData(prev => ({ ...prev, numberOfQuestions: maxQuestions }));
    }
  }, [formData.paperSize, formData.numberOfAnswers, maxQuestions]);
  
  const isFormValid = formData.name.trim().length > 0;

  const handleSubmit = () => {
    if (isFormValid) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleSliderChange = (value: number) => {
    const clampedValue = Math.min(value, maxQuestions);
    setFormData(prev => ({ ...prev, numberOfQuestions: clampedValue }));
  };

  const handleNumberInputChange = (value: number | null) => {
    if (value !== null && value >= 1 && value <= maxQuestions) {
      setFormData(prev => ({ ...prev, numberOfQuestions: value }));
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      width={480}
      closable={false}
      maskClosable={!isLoading}
    >
      <ModalContent>
        <Header>
          <IconContainer>
            <CreateIcon>
              <FileTextOutlined />
            </CreateIcon>
          </IconContainer>
          <Title>Create Answer Sheet</Title>
          <Subtitle>Configure your answer sheet settings</Subtitle>
        </Header>

        <FormSection>
          {/* Sheet Name */}
          <FormRow>
            <Label>Sheet name:</Label>
            <StyledInput
              type="text"
              placeholder="e.g. Math Exam Q1"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isLoading}
            />
          </FormRow>

          {/* Paper Size */}
          <FormRow>
            <Label>Paper size:</Label>
            <SegmentGroup>
              {PAPER_SIZES.map(size => (
                <SegmentButton
                  key={size}
                  $active={formData.paperSize === size}
                  onClick={() => setFormData(prev => ({ ...prev, paperSize: size }))}
                  type="button"
                  disabled={isLoading}
                >
                  {size}
                </SegmentButton>
              ))}
            </SegmentGroup>
          </FormRow>

          {/* Number of Questions */}
          <FormRow>
            <Label>Questions:</Label>
            <SliderRow>
              <Slider
                min={1}
                max={maxQuestions}
                value={formData.numberOfQuestions}
                onChange={handleSliderChange}
                disabled={isLoading}
              />
              <NumberInputStyled
                min={1}
                max={maxQuestions}
                value={formData.numberOfQuestions}
                onChange={handleNumberInputChange}
                controls={false}
                disabled={isLoading}
              />
            </SliderRow>
          </FormRow>

          {/* Number of Answers */}
          <FormRow>
            <Label>Answers:</Label>
            <SegmentGroup>
              {NUMBER_OF_ANSWERS.map(num => (
                <SegmentButton
                  key={num}
                  $active={formData.numberOfAnswers === num}
                  onClick={() => setFormData(prev => ({ ...prev, numberOfAnswers: num as 3 | 4 | 5 }))}
                  type="button"
                  disabled={isLoading}
                >
                  {num}
                </SegmentButton>
              ))}
            </SegmentGroup>
          </FormRow>

          {/* Answer Keys */}
          <FormRow>
            <Label>Answer keys:</Label>
            <SegmentGroup>
              {ANSWER_KEYS.map(key => (
                <SegmentButton
                  key={key}
                  $active={formData.answerKeys === key}
                  onClick={() => setFormData(prev => ({ ...prev, answerKeys: key }))}
                  type="button"
                  disabled={isLoading}
                >
                  {key}
                </SegmentButton>
              ))}
            </SegmentGroup>
          </FormRow>

          {/* Bubble Color */}
          <FormRow>
            <Label>Bubble color:</Label>
            <StyledSelect
              value={formData.bubbleColor}
              onChange={(value) => setFormData(prev => ({ ...prev, bubbleColor: value as AnswerSheetFormData['bubbleColor'] }))}
              options={BUBBLE_COLORS}
              disabled={isLoading}
            />
          </FormRow>
        </FormSection>

        <ButtonSection>
          <CancelButton 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CancelButton>
          
          <CreateButton 
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid}
            loading={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create'}
          </CreateButton>
        </ButtonSection>
      </ModalContent>
    </Modal>
  );
};

export default CreateAnswerSheetModal;
